import L from 'leaflet';
import { dotSprite, drawDotSprite } from './dotSquares.js';
import { areaGrowAt, areaShrinkDelay, listingsGrowDelay } from './areaToggleFade.js';
import { HOME_SHAPE_GAP_PX, HOME_SHAPE_HALO_PX, homeShapePath2D } from './homeTypeIcons.js';
import { sizeCanvas } from './canvasSize.js';

// Regrouping glide when zooming changes the number of listing groups per area, shared by kartvy
// (Map.js) and listvy (ListViewPage.js). The moving markers are drawn on an overlay canvas that
// ignores the mouse, so frames only redraw the markers; the real markers replace it when the glide ends.

export const LISTING_GROUP_ANIMATION_MS = 380;
// Homes appearing or going away at the same zoom (an area clicked on or off): slower than the glide, so the
// shrink into a dot reads.
export const LISTING_PRESENCE_ANIMATION_MS = 650;
// At most this long an ended animation keeps its last frame while the markers it handed over get ready to
// paint (see handOver in createListingGroupAnimator).
const HANDOVER_MAX_MS = 300;
const clamp01 = (value) => Math.min(1, Math.max(0, value));
// Skipped above this many markers.
export const LISTING_GROUP_ANIMATION_MAX_MARKERS = 6000;
export const LISTING_COUNT_FONT_PX = 7;

let listingFontFamily = null;
const getListingFontFamily = () => {
    if (!listingFontFamily) listingFontFamily = window.getComputedStyle(document.body).fontFamily || 'sans-serif';
    return listingFontFamily;
};

// A "shape" marker (markerStyle.js): the home shape (visual.shape) filled with visual.fill and its windows
// and door in visual.detail, inside a white gap and a halo in visual.halo, sized 2 × radius; a count goes on a dark-grey badge with a white number and a ring in the type colour (visual.halo) at the top right.
export const SHAPE_COUNT_BADGE_RADIUS = 7.5;
const drawHomeShape = (ctx, visual, x, y, radius, alpha = 1) => {
    const size = radius * 2;
    const k = size / 24;
    const { body, details } = homeShapePath2D(visual.shape);
    ctx.save();
    // Leaflet leaves the alpha of the (invisible) hit circle it just painted.
    ctx.globalAlpha = alpha;
    ctx.translate(x - radius, y - radius);
    ctx.scale(k, k);
    ctx.lineJoin = 'round';
    // A spread home's dotted leader line is drawn just before it on the same canvas; clear its dash so the
    // halo strokes below aren't drawn dashed (which looked like a scuffed, broken outline).
    ctx.setLineDash([]);
    if (visual.glow) {
        // Hover: a glow in the marker's colour around the halo.
        ctx.save();
        ctx.shadowColor = visual.halo;
        ctx.shadowBlur = 14 * (window.devicePixelRatio || 1);
        ctx.strokeStyle = visual.halo;
        ctx.lineWidth = HOME_SHAPE_HALO_PX / k;
        ctx.stroke(body);
        ctx.stroke(body);
        ctx.restore();
    }
    ctx.strokeStyle = 'rgba(29,43,42,0.32)';
    ctx.lineWidth = (HOME_SHAPE_HALO_PX + 1.4) / k;
    ctx.stroke(body);
    ctx.fillStyle = visual.halo;
    ctx.strokeStyle = visual.halo;
    ctx.lineWidth = HOME_SHAPE_HALO_PX / k;
    ctx.stroke(body);
    ctx.fill(body);
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = HOME_SHAPE_GAP_PX / k;
    ctx.stroke(body);
    ctx.fill(body);
    ctx.fillStyle = visual.fill;
    ctx.fill(body);
    ctx.fillStyle = visual.detail || '#ffffff';
    ctx.fill(details);
    ctx.restore();
    if (visual.count != null) {
        const bx = x + (radius * 0.84);
        const by = y - (radius * 0.84);
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(bx, by, SHAPE_COUNT_BADGE_RADIUS, 0, Math.PI * 2);
        ctx.fillStyle = '#262b2a';
        ctx.fill();
        ctx.lineWidth = 1.6;
        ctx.strokeStyle = visual.halo || '#ffffff';
        ctx.stroke();
        ctx.fillStyle = '#ffffff';
        ctx.font = `700 9px ${getListingFontFamily()}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(visual.count), bx, by + 0.5);
        ctx.restore();
    }
    // Visited (opened in the full view): a small green check badge at the top right. Single homes only, so it
    // never shares the corner with the count badge.
    if (visual.visited) {
        const bx = x + (radius * 0.84);
        const by = y - (radius * 0.84);
        const br = SHAPE_COUNT_BADGE_RADIUS - 1;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(bx, by, br, 0, Math.PI * 2);
        ctx.fillStyle = '#1f7a62';
        ctx.fill();
        ctx.lineWidth = 1.4;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
        ctx.beginPath();
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.moveTo(bx - 2.5, by + 0.1);
        ctx.lineTo(bx - 0.8, by + 1.9);
        ctx.lineTo(bx + 2.6, by - 2.1);
        ctx.stroke();
        ctx.restore();
    }
};

// A rasterized shape-marker sprite for the views' DOM markers, so each marker is a cheap <img> of a shared,
// pre-decoded bitmap instead of its own inline SVG that the browser has to parse and re-rasterize on every
// zoom. One sprite is drawn per distinct look (shape · fill · halo · detail · count · size) and cached. The
// soft base shadow is **baked into the sprite** (SPRITE_SHADOW), so the marker element needs no per-element
// `filter: drop-shadow` — hundreds of those are costly to composite while panning/zooming; the hover glow
// stays a CSS filter (one element at a time, so it's cheap). The box carries extra padding for the shadow.
const SHAPE_SPRITE_PAD_COUNT = 8;
const SHAPE_SPRITE_PAD_PLAIN = 2;
const SPRITE_SHADOW = { color: 'rgba(10, 20, 34, 0.3)', blur: 3, offsetY: 2 }; // = the old .listing-shape-marker filter
const SPRITE_SHADOW_PAD = 6; // room so the baked shadow isn't clipped at the sprite edge
const spriteCache = new Map();

// Draw the marker to a canvas with the base shadow baked in behind it. Returns { canvas, box }.
const renderShapeSpriteCanvas = (visual, size) => {
    const pad = Math.ceil(HOME_SHAPE_HALO_PX / 2)
        + ((visual.count != null || visual.visited) ? SHAPE_SPRITE_PAD_COUNT : SHAPE_SPRITE_PAD_PLAIN)
        + SPRITE_SHADOW_PAD;
    const box = size + (pad * 2);
    const ratio = Math.min(window.devicePixelRatio || 1, 2); // crisp on retina; drawn at `box` CSS px
    const px = Math.max(1, Math.round(box * ratio));
    // The marker itself (no shadow), then composited onto the sprite with a baked drop-shadow behind it.
    const inner = document.createElement('canvas');
    inner.width = inner.height = px;
    const innerCtx = inner.getContext('2d');
    innerCtx.scale(ratio, ratio);
    drawHomeShape(innerCtx, visual, box / 2, box / 2, size / 2);
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = px;
    const ctx = canvas.getContext('2d');
    ctx.shadowColor = SPRITE_SHADOW.color;
    ctx.shadowBlur = SPRITE_SHADOW.blur * ratio;
    ctx.shadowOffsetY = SPRITE_SHADOW.offsetY * ratio;
    ctx.drawImage(inner, 0, 0);
    return { canvas, box };
};

export const homeShapeSprite = (shape, { size, fill, halo, detail = '#ffffff', count = null, visited = false }) => {
    const key = `${shape}:${size}:${fill}:${halo}:${detail}:${count ?? ''}:${visited ? 'v' : ''}`;
    const cached = spriteCache.get(key);
    if (cached) return cached;
    const { canvas, box } = renderShapeSpriteCanvas({ shape, fill, halo, detail, count, visited }, size);
    let url = '';
    try {
        url = canvas.toDataURL('image/png');
    } catch (error) {
        url = '';
    }
    const sprite = { url, box };
    spriteCache.set(key, sprite);
    return sprite;
};

// A cached sprite CANVAS (not a data URL) at a fixed base size, so the glide/presence animation can blit the
// shape with one drawImage per marker per frame instead of re-drawing its ~6 halo/body passes, the details
// fill and the badge's arc + text every frame — about 3× cheaper per marker (measured). One per look, scaled
// to the marker's current radius at blit time; the hover glow isn't baked in, so a glowing marker still takes
// the vector path. Settled markers keep their own exact-size DOM sprite (homeShapeSprite above).
const ANIM_SPRITE_BASE = 30; // radius 15 (the largest marker); smaller markers scale this down, crisply
const animSpriteCache = new Map();
const animSprite = (visual) => {
    const key = `${visual.shape}:${visual.fill}:${visual.halo}:${visual.detail || ''}:${visual.count ?? ''}:${visual.visited ? 'v' : ''}`;
    const cached = animSpriteCache.get(key);
    if (cached) return cached;
    const { canvas, box } = renderShapeSpriteCanvas(visual, ANIM_SPRITE_BASE);
    const sprite = { canvas, box, baseSize: ANIM_SPRITE_BASE };
    animSpriteCache.set(key, sprite);
    return sprite;
};

// Count text drawn on top of a listing marker's circle (or the whole shape marker).
export const drawListingDecorations = (ctx, visual, x, y, radius) => {
    if (visual.shape) {
        drawHomeShape(ctx, visual, x, y, radius);
        return;
    }
    if (visual.count != null) {
        ctx.save();
        ctx.fillStyle = '#ffffff';
        ctx.font = `700 ${LISTING_COUNT_FONT_PX}px ${getListingFontFamily()}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(visual.count), x, y + 0.5);
        ctx.restore();
    }
};

// The sprite each visual is drawn with, looked up once per visual (a WeakMap, so a copy of a visual never
// carries another look's sprite).
const animSpriteByVisual = new WeakMap();
const dotSpriteByVisual = new WeakMap();

// The whole marker. visual: { radius, fill, stroke, weight, count? } or a shape marker's.
export const drawListingVisual = (ctx, visual, x, y, alpha = 1) => {
    if (visual.shape) {
        if (visual.glow) {
            // Hover glow isn't in the sprite, so draw this one as vectors.
            drawHomeShape(ctx, visual, x, y, visual.radius, alpha);
            return;
        }
        // Blit the cached sprite scaled to this radius — far cheaper per frame than re-vectorizing the shape
        // and its number. The current transform (drawMorph's scale) still applies. Looked up once per visual
        // (the cache key is a string built from its look), not on every frame.
        let sprite = animSpriteByVisual.get(visual);
        if (!sprite) {
            sprite = animSprite(visual);
            animSpriteByVisual.set(visual, sprite);
        }
        const draw = sprite.box * ((visual.radius * 2) / sprite.baseSize);
        const prevAlpha = ctx.globalAlpha;
        ctx.globalAlpha = alpha;
        ctx.drawImage(sprite.canvas, x - (draw / 2), y - (draw / 2), draw, draw);
        ctx.globalAlpha = prevAlpha;
        return;
    }
    if (visual.dot) {
        // A zoomed-out home or group: the square from the same sprite the dot canvas draws it with
        // (dotSquares.js), at the ratio this canvas is drawn at, so the hand-over is pixel for pixel.
        const ratio = ctx.__dotRatio || window.devicePixelRatio || 1;
        let memo = dotSpriteByVisual.get(visual);
        if (!memo || memo.ratio !== ratio) {
            memo = { ratio, sprite: dotSprite(visual.fill, visual.stroke, visual.weight, visual.radius, ratio) };
            dotSpriteByVisual.set(visual, memo);
        }
        const { sprite } = memo;
        ctx.globalAlpha = alpha;
        drawDotSprite(ctx, sprite, x, y, ratio);
        drawListingDecorations(ctx, visual, x, y, visual.radius);
        ctx.globalAlpha = 1;
        return;
    }
    ctx.globalAlpha = alpha;
    ctx.setLineDash([]); // clear any dash left by a spread leg so the dot's border isn't drawn dashed.
    ctx.beginPath();
    // Rounded like Leaflet's own canvas circles (Canvas._updateCircle), which the real circle markers are drawn
    // with: a group circle's radius is often a half (clusterIconSize / 2), and drawn unrounded here every circle
    // grew half a pixel when the real ones took over.
    ctx.arc(x, y, Math.max(Math.round(visual.radius), 1), 0, Math.PI * 2);
    ctx.fillStyle = visual.fill;
    ctx.fill();
    if (visual.weight > 0) {
        ctx.lineWidth = visual.weight;
        ctx.strokeStyle = visual.stroke;
        ctx.stroke();
    }
    drawListingDecorations(ctx, visual, x, y, visual.radius);
    ctx.globalAlpha = 1;
};

// A house icon turning into a dot or back (zooming across the zoom where icons start). Only the house scales:
// growing, it swells out of the dot's size while the dot fades; shrinking, it closes down into the dot, which
// fades in under it at its own size.
const drawMorph = (ctx, fromVisual, toVisual, x, y, t) => {
    const growing = Boolean(toVisual.shape);
    const house = growing ? toVisual : fromVisual;
    const dot = growing ? fromVisual : toVisual;
    // How far the house is from dot size (0) to full size (1).
    const size = growing ? t : 1 - t;
    const start = Math.min(1, (dot.radius || 1) / (house.radius || 1));
    const dotAlpha = Math.max(0, 1 - (size * 2.5));
    if (dotAlpha > 0.01) drawListingVisual(ctx, dot, x, y, dotAlpha);
    const houseAlpha = Math.min(1, size * 3);
    if (houseAlpha <= 0.01) return;
    const scale = start + ((1 - start) * size);
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(scale, scale);
    drawListingVisual(ctx, house, 0, 0, houseAlpha);
    ctx.restore();
};

// A marker appearing (presence 0 → 1) or going away (1 → 0), e.g. its area clicked on or off: a house icon
// grows out of a dot that first grows from nothing; a dot or plain circle just grows.
const PRESENCE_DOT_SHARE = 0.3;
const drawPresence = (ctx, visual, x, y, presence) => {
    const dot = visual.shape
        ? { radius: 4.6, dot: true, fill: visual.halo || visual.fill, stroke: '#ffffff', weight: 1 }
        : visual;
    // A plain dot scales over the whole glide; a house's dot only over its first part.
    const dotGrowth = visual.shape ? Math.min(1, presence / PRESENCE_DOT_SHARE) : presence;
    if (!visual.shape || presence <= PRESENCE_DOT_SHARE) {
        if (dotGrowth <= 0.01) return;
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(dotGrowth, dotGrowth);
        drawListingVisual(ctx, dot, 0, 0, Math.min(1, dotGrowth * 1.5));
        ctx.restore();
        return;
    }
    drawMorph(ctx, dot, visual, x, y, (presence - PRESENCE_DOT_SHARE) / (1 - PRESENCE_DOT_SHARE));
};

const entryListings = (entry) => entry.listings || (entry.listing ? [entry.listing] : []);

// Markers that appear or go away without a regroup (an area clicked on or off, a filter change): entries
// added grow in where they are, entries removed shrink away. A new entry that holds homes of a removed one
// is the same marker changed (a neighbouring group that merged differently, or lost a few homes): it moves
// from the old spot to its new one instead of shrinking away and growing back.
export const buildPresenceItems = (addedEntries, removedEntries, getVisual) => {
    const removedByListing = new Map();
    removedEntries.forEach((entry) => {
        entryListings(entry).forEach((listing) => removedByListing.set(listing.id, entry));
    });
    const continued = new Set();
    const items = addedEntries.map((entry) => {
        const previous = entryListings(entry).map((listing) => removedByListing.get(listing.id)).find(Boolean);
        if (!previous) return { from: entry, to: entry, visual: getVisual(entry), presence: 'in', area: entry.areaCode };
        continued.add(previous);
        return { from: { lat: previous.lat, lng: previous.lng }, to: { lat: entry.lat, lng: entry.lng }, visual: getVisual(entry) };
    });
    removedEntries.forEach((entry) => {
        if (!continued.has(entry)) items.push({ from: entry, to: entry, visual: getVisual(entry), presence: 'out', area: entry.areaCode });
    });
    return items;
};

// Moving markers for a change in groups per area. Entries are { lat, lng, listings }; when zooming in
// (splitting), new groups and listings start at the group their first listing came from; when zooming
// out, old groups glide into the group they merge into. Where one end is a house icon and the other a
// dot, the item morphs between them (fromVisual / toVisual).
export const buildGroupAnimationItems = (previousEntries, entries, splitting, getVisual) => {
    const entryByListingId = new Map();
    (splitting ? previousEntries : entries).forEach((entry) => {
        entry.listings.forEach((listing) => entryByListingId.set(listing.id, entry));
    });
    const moving = splitting ? entries : previousEntries;
    return moving.map((entry) => {
        const counterpart = entryByListingId.get(entry.listings[0]?.id);
        const position = { lat: entry.lat, lng: entry.lng };
        const counterpartPosition = counterpart ? { lat: counterpart.lat, lng: counterpart.lng } : position;
        const visual = getVisual(entry);
        const counterpartVisual = counterpart ? getVisual(counterpart) : null;
        const morph = Boolean(counterpartVisual && Boolean(counterpartVisual.shape) !== Boolean(visual.shape));
        return {
            from: splitting ? counterpartPosition : position,
            to: splitting ? position : counterpartPosition,
            visual,
            fromVisual: morph ? (splitting ? counterpartVisual : visual) : null,
            toVisual: morph ? (splitting ? visual : counterpartVisual) : null,
        };
    });
};

// Overlay canvas in its own pane. start(items, onDone) glides items ({ from, to, visual }) and calls
// onDone at the end; finish() ends a running glide straight away (calling onDone), cancel() drops it,
// setOnDone() replaces what runs at the end.
// options.circleCanvas(): where the view draws its plain circle markers — { min (layer point), size (CSS px),
// ratio } of that canvas (kartvy's capped dot canvas). An animation that ends in circles is drawn on the same
// grid, so its last frame is the same pixels as the real circles it hands over to (see start()).
export const createListingGroupAnimator = (map, paneName, zIndex, { circleCanvas } = {}) => {
    const pane = map.getPane(paneName) || map.createPane(paneName);
    pane.style.zIndex = String(zIndex);
    pane.style.pointerEvents = 'none';
    const canvas = document.createElement('canvas');
    canvas.className = 'listing-animation-canvas';
    pane.appendChild(canvas);
    const ctx = canvas.getContext('2d');
    let animation = null;
    // An ended animation's last frame stays up until the markers it handed over have painted (handOver);
    // bumping this drops a pending hold.
    let holdToken = 0;

    const clear = () => {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    // Stops at once: no glide, no held last frame.
    const cancel = () => {
        holdToken += 1;
        if (animation) cancelAnimationFrame(animation.rafId);
        animation = null;
        clear();
    };

    // Ends now (a new zoom, a new update, a re-run): what it was heading for goes on the map. With nothing
    // running it still drops a held last frame, which wouldn't follow the map through a zoom.
    const finish = () => {
        if (!animation) {
            cancel();
            return;
        }
        const { onDone } = animation;
        cancel();
        onDone();
    };

    // The natural end: hand over to the real markers without a blank frame in between. Those can't paint in
    // the frame they're added: a house icon's sprite is a fresh <img> from a data URL, which loads (and on a
    // phone decodes) a frame or more later, and the canvas dots are drawn by their renderer on the next frame.
    // Clearing the overlay at once left a frame with neither — the homes blinked once just after appearing.
    // So the last frame stays up (the real markers draw over it, in the same places) until the new sprites
    // have decoded and two more frames have passed, at most HANDOVER_MAX_MS.
    const handOver = () => {
        const { onDone } = animation;
        cancelAnimationFrame(animation.rafId);
        animation = null;
        onDone();
        holdToken += 1;
        const token = holdToken;
        const decoding = [...map.getContainer().querySelectorAll('.listing-shape-marker img')]
            .filter((img) => !img.__decoded && typeof img.decode === 'function')
            .map((img) => img.decode().then(() => { img.__decoded = true; }, () => {}));
        const ready = decoding.length
            ? Promise.race([Promise.all(decoding), new Promise((resolve) => setTimeout(resolve, HANDOVER_MAX_MS))])
            : Promise.resolve();
        ready.then(() => {
            if (token !== holdToken) return;
            requestAnimationFrame(() => requestAnimationFrame(() => {
                if (token === holdToken && !animation) clear();
            }));
        });
    };

    const start = (items, onDone) => {
        finish();
        // Drawn exactly like what it hands over to. An animation that ends in plain circles uses the geometry of
        // the view's circle canvas — its origin, size and pixel ratio, at the same layer coordinates — so its last
        // frame is the same pixels as the real circles. Drawn on a viewport canvas at the screen's ratio, the
        // packed circles were sharper and half a pixel off from the real ones (whose canvas is capped at 1.5 and
        // starts at its own padded corner), so the whole cluster visibly shifted when they took over: a blink.
        // House icons are DOM sprites at the screen's ratio, so those animations keep a viewport canvas at it.
        const endsInCircles = items.length > 0 && items.every((item) => !(item.toVisual || item.visual)?.shape);
        const circleGeometry = endsInCircles && typeof circleCanvas === 'function' ? circleCanvas() : null;
        const size = circleGeometry ? circleGeometry.size : map.getSize();
        const pixelRatio = circleGeometry ? circleGeometry.ratio : (window.devicePixelRatio || 1);
        sizeCanvas(canvas, ctx, Math.round(size.x * pixelRatio), Math.round(size.y * pixelRatio));
        canvas.style.width = `${size.x}px`;
        canvas.style.height = `${size.y}px`;
        animation = { onDone, rafId: null };
        const presence = items.some((item) => item.presence);
        const glides = items.some((item) => !item.presence);
        // Homes growing in wait for an area being turned on to fade in first (areaToggleFade.js).
        const inDelay = items.some((item) => item.presence === 'in') ? listingsGrowDelay() : 0;
        // In a staggered search change each area's homes have their own start (areaToggleFade.js): read on the
        // first frame after this one, since the selection overlay notes them in an effect that may run just after.
        let delaysResolved = !presence;
        let duration = presence
            ? Math.max(LISTING_PRESENCE_ANIMATION_MS + inDelay, glides ? LISTING_GROUP_ANIMATION_MS : 0)
            : LISTING_GROUP_ANIMATION_MS;
        const resolveDelays = (startTime) => {
            delaysResolved = true;
            let longest = glides ? LISTING_GROUP_ANIMATION_MS : 0;
            items.forEach((item) => {
                if (!item.presence) return;
                if (item.presence === 'in') {
                    const growAt = areaGrowAt(item.area);
                    item.delay = growAt != null ? Math.max(0, growAt - startTime) : inDelay;
                } else {
                    item.delay = areaShrinkDelay(item.area) ?? 0;
                }
                longest = Math.max(longest, item.delay + LISTING_PRESENCE_ANIMATION_MS);
            });
            duration = longest;
        };
        const glideEase = (elapsed) => 1 - ((1 - clamp01(elapsed / LISTING_GROUP_ANIMATION_MS)) ** 3);
        // Appearing and going away ease in and out: a sudden start reads as a jump.
        const presenceEase = (elapsed) => {
            const t = clamp01(elapsed / LISTING_PRESENCE_ANIMATION_MS);
            return t < 0.5 ? 4 * t * t * t : 1 - ((((-2 * t) + 2) ** 3) / 2);
        };
        // Timed from the first frame drawn, so work right after start() (React rendering the change) doesn't
        // eat the beginning.
        let startedAt = null;
        // The zoom and pixel origin the items' layer points were projected for.
        let projectedFor = null;

        const frame = (now) => {
            if (!animation) return;
            if (startedAt == null) startedAt = now;
            const elapsed = now - startedAt;
            if (!delaysResolved && elapsed > 0) resolveDelays(startedAt);
            const eased = glideEase(elapsed);
            clear();
            // Where the canvas sits, in layer coordinates. The pane moves with the map: a viewport canvas is kept
            // over the visible area while it pans; a circle canvas sits where the view's circle canvas does
            // (re-read each frame, since that one re-anchors on moveend).
            const origin = circleGeometry
                ? (circleCanvas() || circleGeometry).min
                : map.containerPointToLayerPoint([0, 0]);
            L.DomUtil.setPosition(canvas, origin);
            ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
            ctx.__dotRatio = pixelRatio; // the dots' sprites are drawn for this ratio (drawListingVisual)
            // Positions follow the map if it moves. An item's layer points only change with the zoom or the
            // pixel origin (a pan leaves them, the canvas's origin moves), so they're projected once per those
            // rather than twice per item per frame.
            const pixelOrigin = map.getPixelOrigin();
            const layerKey = `${map.getZoom()}|${pixelOrigin.x}|${pixelOrigin.y}`;
            if (layerKey !== projectedFor) {
                projectedFor = layerKey;
                items.forEach((item) => {
                    item.fromPoint = map.latLngToLayerPoint(item.from);
                    item.toPoint = item.to === item.from ? item.fromPoint : map.latLngToLayerPoint(item.to);
                });
            }
            items.forEach((item) => {
                const from = item.fromPoint.subtract(origin);
                const to = item.to === item.from ? from : item.toPoint.subtract(origin);
                const x = from.x + ((to.x - from.x) * eased);
                const y = from.y + ((to.y - from.y) * eased);
                if (x < -40 || y < -40 || x > size.x + 40 || y > size.y + 40) return;
                if (item.presence) {
                    // Before the delays are known: new homes not yet there, going ones whole.
                    const delay = item.delay ?? (item.presence === 'in' ? Infinity : 0);
                    const shown = item.presence === 'in' ? presenceEase(elapsed - delay) : 1 - presenceEase(elapsed - delay);
                    drawPresence(ctx, item.visual, x, y, shown);
                }
                else if (item.fromVisual) drawMorph(ctx, item.fromVisual, item.toVisual, x, y, eased);
                else drawListingVisual(ctx, item.visual, x, y);
            });
            if (elapsed < duration) {
                animation.rafId = requestAnimationFrame(frame);
            } else {
                handOver();
            }
        };
        // The first frame at once: the view takes the old markers away now, and a frame without either would
        // flicker.
        frame(performance.now());
    };

    const destroy = () => {
        cancel();
        canvas.remove();
    };

    // A view re-running its marker update mid-animation can let it play on and take over its ending.
    const isRunning = () => Boolean(animation);
    const setOnDone = (onDone) => {
        if (animation) animation.onDone = onDone;
    };

    return { start, finish, cancel, destroy, isRunning, setOnDone };
};
